import { useMemoizedFn } from "ahooks";
import { isEqual } from "lodash";
import { useMemo, useRef } from "react";
import { useQueries } from "react-query";
import { Installation, Plugin } from "types";
import { elrondApi } from "../api/elrondApi";
import { useAutoFetchCollection } from "./useAutoFetchCollection";
import { Options, useInfiniteCollection } from "./useInfiniteCollection";

export const useAutoFetchPlugins = (queryParams?: any) => {
    const params = { limit: 100, ...queryParams };

    return useAutoFetchCollection<Plugin>("plugins", params, {
        keepPreviousData: true,
        enabled: true,
        apiClient: elrondApi,
    });
};

export const useAutoFetchInstallations = (queryParams?: any) => {
    if (!queryParams) {
        queryParams = { status: "completed" };
    }

    const params = { limit: 100, ...queryParams };

    return useAutoFetchCollection("installations", params, {
        keepPreviousData: true,
        enabled: true,
        apiClient: elrondApi,
    });
};

export const usePlugins = (queryParams?: any, options?: Options<Plugin>) => {
    const params = { limit: 100, ...queryParams };

    return useInfiniteCollection<Plugin>("plugins", params, {
        keepPreviousData: true,
        enabled: true,
        apiClient: elrondApi,
        ...options,
    });
};

// Plugin ids differ per environment, so the listing has to be looked up at runtime.
export const useResolveAppStorePath = (isEnabled: boolean) => {
    const { data: plugins = [] } = usePlugins({ status: "approved" }, { enabled: isEnabled });

    return useMemoizedFn((featureId?: string) => {
        const plugin = featureId
            ? plugins.find((candidate) => candidate.abilities?.xolaFeature?.featureId === featureId)
            : undefined;

        return plugin ? `/apps/${plugin.id}` : "/apps";
    });
};

export const useInstallations = (queryParams?: any, options?: Options<Installation>) => {
    if (!queryParams) {
        queryParams = { status: "completed" };
    }

    const params = { limit: 100, ...queryParams };

    return useInfiniteCollection<Installation>("installations", params, {
        keepPreviousData: true,
        enabled: true,
        apiClient: elrondApi,
        ...options,
    });
};

export const useInstalledPlugins = () => {
    const { data: installations = [], isLoading: isLoadingInstallations } = useInstallations(
        { status: "completed" },
        { retryOnMount: false },
    );
    const { data: publicPlugins = [], isLoading: isLoadingPlugins } = usePlugins(
        { status: "approved" },
        { retryOnMount: false },
    );
    const { data: privatePlugins = [], isLoading: isLoadingPrivatePlugins } = useFetchPrivateInstalledPlugins(
        installations,
        publicPlugins,
    );

    // Memoized so consumers get a stable array reference across renders when the underlying data is
    // unchanged, rather than a fresh filtered array every render.
    const data = useMemo(() => {
        const plugins = [...publicPlugins, ...privatePlugins] as Plugin[];
        return plugins.filter((plugin) => installations.some((installation) => installation.plugin === plugin.id));
    }, [installations, publicPlugins, privatePlugins]);

    return { data, isLoading: isLoadingPlugins || isLoadingInstallations || isLoadingPrivatePlugins };
};

// The serializer for `abilities.messaging.enabled` can return either a bool or a stringified bool depending on
// the plugin path. Until the API contract is stabilized, accept both shapes.
export const hasMessagingAbility = (plugin: Plugin) => {
    const enabled = plugin.abilities?.messaging?.enabled;
    return enabled === "true" || enabled === true;
};

export const useMessagingPlugins = (): Plugin[] => {
    const { data: installedPlugins = [] } = useInstalledPlugins();
    return installedPlugins.filter((plugin) => hasMessagingAbility(plugin));
};

/** Returns the installed Airbnb plugin if it has messaging ability, otherwise null. */
export const useAirbnbMessagingPlugin = (): Plugin | null => {
    const { data: installedPlugins = [] } = useInstalledPlugins();
    return (
        installedPlugins.find((plugin) => plugin.name.toLowerCase() === "airbnb" && hasMessagingAbility(plugin)) ?? null
    );
};

/**
 * The issue here is that GET /plugins doesn't return private plugins but GET plugin/{id} will return it
 * So for any installation which doesn't have a corresponding plugin in the cache we need to call the GET plugins/{id}
 */
const useFetchPrivateInstalledPlugins = (installations?: Installation[], plugins?: Plugin[]) => {
    const missingPlugins = [];
    for (const installation of installations ?? []) {
        // Don't process this list until all plugins are fetched
        if (plugins?.length === 0) continue;

        const plugin = plugins?.find((plugin) => plugin.id === installation.plugin);
        if (!plugin) {
            missingPlugins.push({
                queryKey: ["plugin", installation.plugin],
                queryFn: async () => {
                    const { data } = await elrondApi.get<Plugin>(`/plugins/${installation.plugin}`);
                    return data ?? {};
                },
                retryOnMount: false,
            });
        }
    }

    // This will issue multiple requests to GET /plugins/{id} for each missing plugin all together
    const queries = useQueries(missingPlugins);

    // useQueries hands back a fresh array every render; return the prior reference on unchanged
    // content so downstream memoization (useInstalledPlugins) stays stable.
    const dataRef = useRef<Plugin[]>([]);
    const nextData = queries.map((q) => (q.data ?? {}) as Plugin);
    if (!isEqual(nextData, dataRef.current)) {
        dataRef.current = nextData;
    }

    return { data: dataRef.current, isLoading: queries.some((q) => q.isLoading) };
};
